import { EditorState, StateEffect, StateField, Transaction } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { CommentList, TextOperation, TrackingProps, TrackedChangeList } from "components/organisms/editor/core/model";
import {
    buildTrackedCommentDecorations,
    ChangeDeletedWidget,
} from "components/organisms/editor/history/ranges/decorations";
import { trackChangesTheme } from "components/organisms/editor/history/ranges/theme";
import { TrackedDeletes, trackedDeletesFromState } from "components/organisms/editor/history/trackedDeletes";

export function historyOT(currentDoc) {
    const trackedChanges = currentDoc?.historyOTShareDoc?.snapshot?.getTrackedChanges?.() ?? new TrackedChangeList([]);
    const comments = normalizeComments(currentDoc?.historyOTShareDoc?.snapshot?.getComments?.() ?? new CommentList([]));

    return [
        updateSender,
        trackChangesUserIdState,
        shareDocState.init(() => currentDoc?.doc?._doc ?? null),
        rangesState.init(() => ({
            trackedChanges,
            comments,
            decorations: buildRangesDecorations({ trackedChanges, comments }),
        })),
        trackChangesTheme,
    ];
}

export const shareDocState = StateField.define({
    create() {
        return null;
    },
    update(value) {
        return value;
    },
});

export const rangesUpdatedEffect = StateEffect.define();

function normalizeComments(comments) {
    if (comments?.toArray) {
        return comments.toArray();
    }

    return Array.isArray(comments) ? comments : [];
}

function buildRangesDecorations({ trackedChanges, comments }) {
    const trackedDeletes = new TrackedDeletes(trackedChanges);
    return buildTrackedCommentDecorations({
        trackedChanges,
        comments,
        toCodeMirror: (position) => trackedDeletes.toCodeMirror(position),
    });
}

export const rangesState = StateField.define({
    create() {
        const trackedChanges = new TrackedChangeList([]);
        const comments = [];
        const decorations = buildRangesDecorations({ trackedChanges, comments });
        return { trackedChanges, comments, decorations };
    },

    update(value, transaction) {
        const shareDoc = transaction.state.field(shareDocState);
        const snapshot = shareDoc?.snapshot;
        let shouldRebuild = transaction.docChanged;
        let shouldNotifyRangesDirty = false;

        for (const effect of transaction.effects) {
            if (effect.is(rangesUpdatedEffect)) {
                shouldRebuild = true;
                shouldNotifyRangesDirty = true;
            }
        }

        if (shouldRebuild) {
            const trackedChanges = snapshot?.getTrackedChanges?.() ?? value.trackedChanges;
            const comments = normalizeComments(snapshot?.getComments?.() ?? []);
            const decorations = buildRangesDecorations({ trackedChanges, comments });
            value = { trackedChanges, comments, decorations };

            if (shouldNotifyRangesDirty) {
                shareDoc?.emit?.("ranges:dirty");
            }
        }

        return value;
    },

    provide(field) {
        return EditorView.decorations.from(field, (value) => value.decorations);
    },
});

const setTrackChangesUserIdEffect = StateEffect.define();

export function setTrackChangesUserId(userId) {
    return {
        effects: setTrackChangesUserIdEffect.of(userId),
    };
}

const trackChangesUserIdState = StateField.define({
    create() {
        return null;
    },

    update(value, transaction) {
        for (const effect of transaction.effects) {
            if (effect.is(setTrackChangesUserIdEffect)) {
                value = effect.value;
            }
        }
        return value;
    },
});

const updateSender = EditorState.transactionExtender.of((transaction) => {
    if (!transaction.docChanged || transaction.annotation(Transaction.remote)) {
        return {};
    }

    const trackingUserId = transaction.startState.field(trackChangesUserIdState);
    const trackedDeletes = trackedDeletesFromState(transaction.startState);
    const startDoc = transaction.startState.doc;
    const opBuilder = new OperationBuilder(trackedDeletes.toSnapshot(startDoc.length));

    if (trackingUserId == null) {
        transaction.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
            if (inserted.length > 0) {
                opBuilder.insert(trackedDeletes.toSnapshot(fromA), inserted.toString());
            }
            if (toA > fromA) {
                const start = trackedDeletes.toSnapshot(fromA);
                const end = trackedDeletes.toSnapshot(toA);
                opBuilder.delete(start, end - start);
            }
        });
    } else {
        const timestamp = new Date();
        transaction.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
            if (inserted.length > 0) {
                opBuilder.trackedInsert(
                    trackedDeletes.toSnapshot(fromA),
                    inserted.toString(),
                    trackingUserId,
                    timestamp,
                );
            }
            if (toA > fromA) {
                const start = trackedDeletes.toSnapshot(fromA);
                const end = trackedDeletes.toSnapshot(toA);
                opBuilder.trackedDelete(start, end - start, trackingUserId, timestamp);
            }
        });
    }

    const op = opBuilder.finish();
    const shareDoc = transaction.startState.field(shareDocState);
    if (shareDoc != null) {
        shareDoc.submitOp([op]);
    }

    return {};
});

class OperationBuilder {
    constructor(docLength) {
        this.docLength = docLength;
        this.pos = 0;
        this.op = new TextOperation();
    }

    insert(pos, text) {
        this.retainUntil(pos);
        this.op.insert(text);
    }

    delete(pos, length) {
        this.retainUntil(pos);
        this.op.remove(length);
        this.pos += length;
    }

    trackedInsert(pos, text, userId, timestamp) {
        this.retainUntil(pos);
        this.op.insert(text, {
            tracking: new TrackingProps("insert", userId, timestamp),
        });
    }

    trackedDelete(pos, length, userId, timestamp) {
        this.retainUntil(pos);
        this.op.retain(length, {
            tracking: new TrackingProps("delete", userId, timestamp),
        });
        this.pos += length;
    }

    retainUntil(pos) {
        if (pos > this.pos) {
            this.op.retain(pos - this.pos);
            this.pos = pos;
            return;
        }
        if (pos < this.pos) {
            throw new Error(`Out of order: position ${pos} comes before current position: ${this.pos}`);
        }
    }

    finish() {
        this.retainUntil(this.docLength);
        return this.op;
    }
}

export default historyOT;
